<template>
  <lab-dialog :visible="visible" :title="dialogTitle" size="wide" @close="close">
    <section class="policy-task-assignment-dialog">
      <operation-message :value="notice" :type="failed ? 'error' : 'success'" @consumed="notice = ''" />
      <div v-if="caseItem" class="policy-task-context">
        <strong>{{ caseItem.name }}</strong>
        <span>在当前案例中创建分配任务，并查看学生完成情况</span>
      </div>
      <div v-if="loading" class="lab-empty">正在读取任务列表与完成情况…</div>
      <template v-else-if="form">
        <div class="policy-task-form-heading">
          <div>
            <h4>{{ form.id ? '编辑分配任务' : '创建分配任务' }}</h4>
            <p class="lab-hint">任务创建后会同步到真实学生，案例和评分规则按分配时版本冻结。</p>
          </div>
          <button type="button" class="btn small" :disabled="busy" @click="closeForm">返回任务列表</button>
        </div>
        <fieldset class="module-fieldset" :disabled="busy">
          <div class="lab-form-grid policy-task-form">
            <label class="lab-field module-wide-field">任务名称 <span class="req">*</span><input v-model.trim="form.name" maxlength="150" placeholder="如：政策模拟演练（第1期）"></label>
            <label class="lab-field module-wide-field">实训案例<input :value="caseItem && caseItem.name" readonly></label>
            <label class="lab-field">实训时长（分钟）<span class="req">*</span><input v-model.number="form.duration" type="number" min="1" max="1440" step="1"></label>
            <label class="lab-field">截止日期<input v-model="form.deadline" type="datetime-local"></label>
            <label class="lab-field">允许下载报告<select v-model="form.allowDownload"><option :value="true">允许下载</option><option :value="false">禁止下载</option></select></label>
            <label class="lab-field">允许学生重做<select v-model="form.allowRedo"><option :value="false">不允许重做</option><option :value="true">允许重做</option></select><span class="lab-hint">重做会新建一次实训，旧成绩和报告保留。</span></label>
            <label class="lab-field module-wide-field">分配范围 <span class="req">*</span><select v-model="form.targets.type"><option value="class">按班级分配（可多选）</option><option value="student">按学生分配（可多选）</option><option value="all">本人授权范围全体学生</option></select></label>
            <div v-if="form.targets.type === 'class'" class="lab-field module-wide-field policy-task-targets"><span>选择班级</span><div class="policy-task-checks"><label v-for="c in classes" :key="c.id" class="lab-check"><input v-model="form.targets.classIds" type="checkbox" :value="c.id">{{ c.name }}</label></div><span v-if="!classes.length" class="lab-warning">没有授权班级，请联系管理员配置教学范围。</span></div>
            <div v-else-if="form.targets.type === 'student'" class="lab-field module-wide-field policy-task-targets"><span>选择学生</span><div class="policy-task-checks"><label v-for="s in students" :key="s.id" class="lab-check"><input v-model="form.targets.stuIds" type="checkbox" :value="s.id">{{ s.name }} · {{ s.stuNo }}</label></div><span v-if="!students.length" class="lab-warning">当前教学范围没有启用学生。</span></div>
            <div v-else class="lab-note module-wide-field">将分配给本人授权范围内 {{ students.length }} 名启用学生，不包含未授权学生。</div>
          </div>
        </fieldset>
        <p v-if="form.hasStarted" class="lab-note lab-spaced">已有实训记录：案例版本锁定；可增加学生或移除未开始学生，不能移除已有记录的学生。</p>
        <p v-else class="lab-hint lab-spaced">创建即校验并下发到真实学生，不需要再发布草稿。</p>
      </template>
      <template v-else>
        <div class="policy-task-tabs" role="tablist" aria-label="任务信息">
          <button type="button" class="policy-task-tab" :class="{ active: tab === 'tasks' }" role="tab" :aria-selected="tab === 'tasks'" @click="tab = 'tasks'">任务列表 <span>{{ caseTasks.length }}</span></button>
          <button type="button" class="policy-task-tab" :class="{ active: tab === 'completion' }" role="tab" :aria-selected="tab === 'completion'" @click="tab = 'completion'">任务完成情况</button>
        </div>
        <template v-if="tab === 'tasks'">
          <div class="policy-task-toolbar"><p class="lab-note">当前仅显示此案例的正式分配任务；任务修改、权限开关和删除均会同步实际学生任务。</p><button type="button" class="btn primary small" :disabled="busy || !caseItem" @click="openCreate">+ 创建分配任务</button></div>
          <div class="lab-table-wrap policy-task-table-wrap">
            <table class="lab-table policy-task-table">
              <thead><tr><th>任务名称</th><th>案例</th><th>时长</th><th>允许下载</th><th>允许重做</th><th>分配范围</th><th>截止</th><th>状态</th><th>操作</th></tr></thead>
              <tbody>
                <tr v-for="task in caseTasks" :key="task.id">
                  <td class="policy-task-name"><strong>{{ task.name }}</strong></td>
                  <td>{{ task.caseName || (caseItem && caseItem.name) }}</td>
                  <td>{{ task.duration }} 分钟</td>
                  <td><span class="lab-tag" :class="task.allowDownload ? 'green' : 'gray'">{{ task.allowDownload ? '允许' : '禁止' }}</span></td>
                  <td><span class="lab-tag" :class="task.allowRedo ? 'green' : 'gray'">{{ task.allowRedo ? '允许' : '禁止' }}</span></td>
                  <td>{{ task.rangeText || '—' }}</td>
                  <td>{{ date(task.deadline) }}</td>
                  <td><span class="lab-tag" :class="statusClass(task.status)">{{ task.status }}</span></td>
                  <td><div class="policy-task-actions"><button type="button" class="btn small" :disabled="busy" @click="edit(task)">编辑</button><button type="button" class="btn small" :disabled="busy" @click="download(task)">下载开关</button><button type="button" class="btn small" :disabled="busy" @click="redo(task)">重做开关</button><button type="button" class="btn small lab-danger" :disabled="busy" @click="remove(task)">删除</button></div></td>
                </tr>
                <tr v-if="!caseTasks.length"><td colspan="9" class="lab-empty">当前案例暂无分配任务，点击右上角“创建分配任务”。</td></tr>
              </tbody>
            </table>
          </div>
        </template>
        <template v-else>
          <div class="policy-task-completion-head"><div><strong>任务完成情况</strong><p class="lab-hint">按当前案例汇总每个分配任务的最新有效完成轮次。</p></div><button type="button" class="btn small" :disabled="busy" @click="load(false)">刷新</button></div>
          <div class="lab-table-wrap policy-task-table-wrap policy-task-completion-table-wrap">
            <table class="lab-table policy-task-table">
              <thead><tr><th>任务名称</th><th>分配范围</th><th>完成情况</th><th>完成率</th><th>状态</th></tr></thead>
              <tbody>
                <tr v-for="task in caseTasks" :key="task.id">
                  <td class="policy-task-name"><strong>{{ task.name }}</strong></td>
                  <td>{{ task.rangeText || '—' }}</td>
                  <td>{{ completion(task).done }} / {{ completion(task).total }}</td>
                  <td><div class="policy-task-percent"><div class="policy-meter" role="progressbar" :aria-label="task.name + '完成率'" :aria-valuenow="completion(task).percent" aria-valuemin="0" aria-valuemax="100"><i :style="{ width: completion(task).percent + '%' }"></i></div><span>{{ completion(task).percent }}%</span></div></td>
                  <td><span class="lab-tag" :class="statusClass(task.status)">{{ task.status }}</span></td>
                </tr>
                <tr v-if="!caseTasks.length"><td colspan="5" class="lab-empty">暂无任务完成情况。</td></tr>
              </tbody>
            </table>
          </div>
        </template>
      </template>
    </section>
    <template #footer>
      <template v-if="form"><button type="button" class="btn small" :disabled="busy" @click="closeForm">取消</button><button type="button" class="btn primary small" :disabled="busy" @click="save">{{ form.id ? '保存任务' : '创建任务' }}</button></template>
      <button v-else type="button" class="btn small" :disabled="busy" @click="close">关闭</button>
    </template>
  </lab-dialog>
</template>

<script>
import LabDialog from './NetworkLabDialog.vue'
import { api, errorText } from '../services/api'

const base = '/policy/tasks'
const requestId = () => 'policy-task-' + Date.now() + '-' + Math.random().toString(36).slice(2)
const clone = value => JSON.parse(JSON.stringify(value))

export default {
  name: 'PolicyTaskAssignmentDialog',
  components: { LabDialog },
  props: { visible: Boolean, caseItem: { type: Object, default: null } },
  data () { return { tab: 'tasks', tasks: [], classes: [], students: [], loading: false, busy: false, notice: '', failed: false, form: null, timer: null } },
  computed: {
    dialogTitle () { return '分配任务 · ' + (this.caseItem ? this.caseItem.name : '案例') },
    caseTasks () {
      if (!this.caseItem) return []
      const caseIds = [this.caseItem.id, this.caseItem.publishedCaseId].filter(Boolean).map(String)
      return this.tasks.filter(task => {
        if (task.kind === 'EXPERIENCE') return false
        const taskIds = [task.caseId, task.sourcePolicyCaseId, task.sourceCaseId, task.caseSourceId].filter(Boolean).map(String)
        return taskIds.some(id => caseIds.includes(id)) || String(task.caseName || '') === String(this.caseItem.name || '')
      })
    }
  },
  watch: {
    visible (value) { if (value) { this.tab = 'tasks'; this.form = null; this.load() } },
    caseItem (value) { if (value && this.visible) this.load() }
  },
  created () {
    if (this.visible) this.load()
    this.timer = window.setInterval(() => { if (this.visible && !this.busy && !this.form) this.load(false) }, 30000)
  },
  beforeDestroy () { window.clearInterval(this.timer) },
  methods: {
    async load (showLoading = true) {
      if (showLoading) this.loading = true
      try {
        const data = await api.get(base)
        this.tasks = data.tasks || []
        this.classes = data.classes || []
        this.students = data.students || []
      } catch (e) { this.fail(e) } finally { this.loading = false }
    },
    openCreate () {
      if (!this.caseItem) return
      this.failed = false
      this.form = { name: this.caseItem.name + ' · 实验任务', caseId: this.caseItem.id, caseRevision: this.caseItem.revision, duration: this.caseItem.duration || 90, deadline: '', allowDownload: true, allowRedo: false, targets: { type: 'class', classIds: [], stuIds: [] }, requestId: requestId() }
    },
    edit (task) {
      this.failed = false
      const form = clone(task)
      form.deadline = this.localDate(task.deadline)
      form.targets = this.normalizeTargets(task)
      form.requestId = requestId()
      this.form = form
    },
    normalizeTargets (task) {
      const source = task.targets && typeof task.targets === 'object' ? task.targets : {}
      return { type: source.type || 'student', classIds: Array.isArray(source.classIds) ? source.classIds.slice() : [], stuIds: Array.isArray(source.stuIds) ? source.stuIds.slice() : [] }
    },
    close () { if (!this.busy) { this.form = null; this.$emit('close') } },
    closeForm () { if (!this.busy) this.form = null },
    async save () {
      await this.job(async () => {
        const body = clone(this.form)
        body.deadline = body.deadline ? new Date(body.deadline).toISOString() : ''
        body.targets = this.normalizeTargets(body)
        if (!body.id) { body.caseId = this.caseItem.id; if (this.caseItem.revision !== undefined) body.caseRevision = this.caseItem.revision }
        if (body.id) await api.put(base + '/' + body.id, body); else await api.post(base, body)
        this.form = null
        this.notice = body.id ? '任务已保存，实际学生任务已同步。' : '任务已创建并同步到实际学生，案例和评分使用冻结版本。'
        this.failed = false
        await this.load(false)
      })
    },
    async download (task) {
      await this.job(async () => { await api.post(base + '/' + task.id + '/download', { revision: task.revision, allowDownload: !task.allowDownload, requestId: requestId() }); this.notice = task.allowDownload ? '已关闭学生报告下载权限。' : '已开放学生报告下载权限。'; await this.load(false) })
    },
    async redo (task) {
      await this.job(async () => { const body = clone(task); body.allowRedo = !task.allowRedo; body.requestId = requestId(); await api.put(base + '/' + task.id, body); this.notice = task.allowRedo ? '已关闭学生重做权限。' : '已允许学生重做，旧成绩和报告保留。'; await this.load(false) })
    },
    async remove (task) {
      if (!await this.$messageBox.confirm('删除任务将移除学生任务入口并终止未完成运行，历史过程、报告和成绩仍保留，确认删除？', { title: '删除任务', confirmText: '确认删除', danger: true })) return
      await this.job(async () => { await api.post(base + '/' + task.id + '/delete', { revision: task.revision, requestId: requestId() }); this.notice = '任务已移除，历史过程和成绩保留。'; await this.load(false) })
    },
    completion (task) { return task.completion || { done: 0, total: 0, percent: 0 } },
    statusClass (status) { return status === '已结束' ? 'green' : status === '草稿' ? 'gray' : status === '已截止' ? 'gray' : 'orange' },
    localDate (value) { if (!value) return ''; const d = new Date(value); if (!Number.isFinite(d.getTime())) return ''; const z = n => String(n).padStart(2, '0'); return d.getFullYear() + '-' + z(d.getMonth() + 1) + '-' + z(d.getDate()) + 'T' + z(d.getHours()) + ':' + z(d.getMinutes()) },
    date (value) { return value ? this.localDate(value).replace('T', ' ') || value : '—' },
    async job (fn) { if (this.busy) return; this.busy = true; this.failed = false; try { await fn() } catch (e) { this.fail(e) } finally { this.busy = false } },
    fail (e) { this.notice = errorText(e); this.failed = true }
  }
}
</script>
